/**
 * SearchableSelect — looks like a native <select>, types to filter like macOS.
 *
 * No visible search bar. When the dropdown opens it auto-captures keystrokes
 * and filters options in real-time. Pressing Escape or clicking outside closes.
 */
import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type KeyboardEvent,
} from 'react';
import { CaretDown, Check } from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

export interface SelectOption {
  value: string;
  label: string;
  sub?: string;        // optional secondary line (e.g. email, code)
  disabled?: boolean;
}

interface Props {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  /** Max height of the dropdown list in px */
  maxHeight?: number;
}

export function SearchableSelect({
  value,
  onChange,
  options,
  placeholder = 'Select…',
  disabled = false,
  className,
  maxHeight = 240,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const hiddenInputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [highlightedIdx, setHighlightedIdx] = useState<number>(-1);

  const selectedOption = options.find((o) => o.value === value);

  // Filter options by query
  const filtered = query.trim()
    ? options.filter((o) => {
        const hay = `${o.label} ${o.sub ?? ''}`.toLowerCase();
        return hay.includes(query.toLowerCase());
      })
    : options;

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
    setHighlightedIdx(-1);
  }, []);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, close]);

  // Focus hidden input when dropdown opens to capture keystrokes
  useEffect(() => {
    if (open) {
      hiddenInputRef.current?.focus();
      // Pre-highlight the currently selected option
      const idx = filtered.findIndex((o) => o.value === value);
      setHighlightedIdx(idx >= 0 ? idx : 0);
    }
  }, [open]);

  // Scroll highlighted option into view
  useEffect(() => {
    if (highlightedIdx < 0 || !listRef.current) return;
    const el = listRef.current.children[highlightedIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ block: 'nearest' });
  }, [highlightedIdx]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.min(i + 1, filtered.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightedIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const opt = filtered[highlightedIdx];
      if (opt && !opt.disabled) {
        onChange(opt.value);
        close();
      }
      return;
    }
    if (e.key === 'Backspace') {
      setQuery((q) => q.slice(0, -1));
      setHighlightedIdx(0);
      return;
    }
    // Printable character — append to query
    if (e.key.length === 1) {
      setQuery((q) => q + e.key);
      setHighlightedIdx(0);
    }
  };

  const handleSelect = (opt: SelectOption) => {
    if (opt.disabled) return;
    onChange(opt.value);
    close();
  };

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      {/* Trigger */}
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={cn(
          'flex h-9 w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 text-sm transition',
          'focus:outline-none focus:ring-1 focus:ring-brand-600',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-brand-400',
          open && 'border-brand-500 ring-1 ring-brand-500',
        )}
      >
        <span className={cn('flex-1 truncate text-left', !selectedOption && 'text-muted-foreground')}>
          {query && open
            ? <span className="text-foreground">{query}<span className="text-muted-foreground/60">…</span></span>
            : (selectedOption?.label ?? placeholder)}
        </span>
        <CaretDown
          size={13}
          weight="bold"
          className={cn('shrink-0 text-muted-foreground transition', open && 'rotate-180')}
        />
      </button>

      {/* Hidden input captures keystrokes while dropdown is open */}
      <input
        ref={hiddenInputRef}
        tabIndex={-1}
        className="absolute inset-0 h-0 w-0 opacity-0 pointer-events-none"
        onKeyDown={handleKeyDown}
        readOnly
        aria-hidden
      />

      {/* Dropdown list */}
      {open && (
        <div
          className="absolute left-0 right-0 top-[calc(100%+3px)] z-50 overflow-hidden rounded-lg border border-border bg-card shadow-lg"
          style={{ maxHeight }}
        >
          {/* Inline query display — subtle, not a labelled search bar */}
          {query && (
            <div className="flex items-center gap-1.5 border-b border-border/60 bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
              <span className="italic">Filtering: </span>
              <span className="font-medium text-foreground">{query}</span>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); setQuery(''); setHighlightedIdx(0); hiddenInputRef.current?.focus(); }}
                className="ml-auto text-muted-foreground/60 hover:text-foreground"
              >
                ×
              </button>
            </div>
          )}
          <ul ref={listRef} className="overflow-y-auto" style={{ maxHeight: query ? maxHeight - 36 : maxHeight }}>
            {filtered.length === 0 ? (
              <li className="px-4 py-4 text-center text-xs text-muted-foreground">
                No options match "{query}"
              </li>
            ) : (
              filtered.map((opt, idx) => (
                <li key={opt.value}>
                  <button
                    type="button"
                    disabled={opt.disabled}
                    onMouseDown={(e) => { e.preventDefault(); handleSelect(opt); }}
                    className={cn(
                      'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition',
                      idx === highlightedIdx && 'bg-accent',
                      opt.disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-muted/50',
                      opt.value === value && 'font-medium',
                    )}
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{opt.label}</span>
                      {opt.sub && (
                        <span className="block truncate text-xs text-muted-foreground">{opt.sub}</span>
                      )}
                    </span>
                    {opt.value === value && (
                      <Check size={13} weight="bold" className="shrink-0 text-brand-600 dark:text-brand-400" />
                    )}
                  </button>
                </li>
              ))
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
